/**
 * Parcelle Referral System
 * Shared JS for referral landing and referral start pages.
 * Replaces jQuery-based logic with vanilla JS + fetch.
 */

const ParcellReferral = (function () {
  'use strict';

  /**
   * Call the referral API
   * @param {string} baseUrl - API base URL
   * @param {string} method - HTTP method
   * @param {string} endpoint - API path after /referrals/
   * @param {object} params - query params (for GET) or body (for POST)
   * @returns {Promise<object>}
   */
  async function callAPI(baseUrl, method, endpoint, params) {
    let url = baseUrl.replace(/\/$/, '') + '/referrals/' + endpoint;

    if (method === 'GET' && params) {
      const queryString = new URLSearchParams(params).toString();
      url += (url.includes('?') ? '&' : '?') + queryString;
    }

    try {
      const options = {
        method: method,
        headers: { 'Content-Type': 'application/json' }
      };

      if (method === 'POST' && params) {
        options.body = JSON.stringify(params);
      }

      const response = await fetch(url, options);
      const data = await response.json();
      return data;
    } catch (err) {
      console.error('[ParcellReferral] API error:', err);
      return null;
    }
  }

  /**
   * Generate a discount code from a referral code
   * @param {string} baseUrl - API base URL
   * @param {string} refCode - referral code from URL param
   * @param {string} userEmail - current customer email
   * @returns {Promise<object>}
   */
  async function getDiscountCode(baseUrl, refCode, userEmail) {
    if (!refCode) return null;
    const endpoint = 'generate_discount?ref=' + encodeURIComponent(refCode) + '&user=' + encodeURIComponent(userEmail || '');
    return await callAPI(baseUrl, 'GET', endpoint, null);
  }

  /**
   * Track a referral visit
   * @param {string} baseUrl - API base URL
   * @param {string} refCode - referral code
   * @param {string} userEmail - current customer email
   * @returns {Promise<object>}
   */
  async function trackVisit(baseUrl, refCode, userEmail) {
    const endpoint = 'visit_site?ref=' + encodeURIComponent(refCode) + '&user=' + encodeURIComponent(userEmail || '');
    return await callAPI(baseUrl, 'GET', endpoint, null);
  }

  /**
   * Create a referral code for a user
   * @param {string} baseUrl - API base URL
   * @param {string} email - user's email
   * @param {string} name - user's name (optional)
   * @returns {Promise<object>}
   */
  async function createRefCode(baseUrl, email, name) {
    if (!email || email.length < 4) return null;
    const endpoint = 'create_ref_code?email=' + encodeURIComponent(email) + '&name=' + encodeURIComponent(name || '');
    return await callAPI(baseUrl, 'GET', endpoint, null);
  }

  /**
   * Copy text from an input field to clipboard
   * @param {string} inputId - the input element ID
   * @returns {boolean}
   */
  function copyToClipboard(inputId) {
    const input = document.getElementById(inputId);
    if (!input) return false;

    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(input.value).catch(function () {
        fallbackCopy(input);
      });
    } else {
      fallbackCopy(input);
    }
    return true;
  }

  function fallbackCopy(input) {
    input.select();
    input.setSelectionRange(0, 99999);
    document.execCommand('copy');
  }

  /**
   * Initialize the Referral Landing page
   * @param {object} config
   */
  function initLanding(config) {
    const {
      baseUrl,
      customerEmail,
      shopDomain,
      personalizedEl,
      discountInputEl,
      shopNowBtn,
      copiedOverlay,
      defaultGreeting,
      referrerGreeting
    } = config;

    const urlParams = new URLSearchParams(window.location.search);
    const refCode = urlParams.get('rc');

    // Fetch discount code on load
    if (refCode) {
      getDiscountCode(baseUrl, refCode, customerEmail).then(function (result) {
        if (!result || !result.data) return;

        if (discountInputEl) {
          discountInputEl.value = result.data.discountCode || '';
        }

        if (personalizedEl) {
          if (result.data.name && result.data.name.length > 1 && result.data.name !== 'undefined') {
            personalizedEl.innerHTML = '<span>' + (referrerGreeting || '{name} shared Parcelle Picks with you!').replace('{name}', result.data.name) + '</span>';
          } else {
            personalizedEl.innerHTML = '<span>' + (defaultGreeting || 'Thanks for Checking out Parcelle Picks!') + '</span>';
          }
        }
      });
    }

    // Shop Now click handler
    if (shopNowBtn) {
      shopNowBtn.addEventListener('click', async function (e) {
        e.preventDefault();
        const code = discountInputEl ? discountInputEl.value : '';

        if (code) {
          await trackVisit(baseUrl, code, customerEmail);
          copyToClipboard(discountInputEl.id);

          if (copiedOverlay) {
            copiedOverlay.style.display = 'block';
            copiedOverlay.style.opacity = '1';
          }

          setTimeout(function () {
            window.location = 'https://' + shopDomain + '/discount/' + code;
          }, 600);
        }
      });
    }
  }

  /**
   * Initialize the Refer a Friend (start) page
   * @param {object} config
   */
  function initStart(config) {
    const {
      baseUrl,
      getRefSection,
      showRefSection,
      emailInput,
      nameInput,
      referralUrlInput,
      getLinkBtn,
      copyBtn,
      copiedOverlay
    } = config;

    // Get Link button handler
    if (getLinkBtn) {
      getLinkBtn.addEventListener('click', async function (e) {
        e.preventDefault();
        const email = emailInput ? emailInput.value : '';
        const name = nameInput ? nameInput.value : '';

        if (!email || email.length < 4) return;

        const result = await createRefCode(baseUrl, email, name);
        if (result && result.data && result.data.refCode) {
          if (getRefSection) {
            getRefSection.style.display = 'none';
          }
          if (referralUrlInput) {
            referralUrlInput.value = window.location.origin + '/ref?rc=' + result.data.refCode;
          }
          if (showRefSection) {
            showRefSection.style.display = 'block';
          }
        }
      });
    }

    // Copy button handler
    if (copyBtn) {
      copyBtn.addEventListener('click', function (e) {
        e.preventDefault();
        if (referralUrlInput) {
          copyToClipboard(referralUrlInput.id);
          if (copiedOverlay) {
            copiedOverlay.style.display = 'block';
            copiedOverlay.style.opacity = '1';
            setTimeout(function () {
              copiedOverlay.style.opacity = '0';
              setTimeout(function () {
                copiedOverlay.style.display = 'none';
              }, 400);
            }, 2000);
          }
        }
      });
    }
  }

  return {
    callAPI: callAPI,
    getDiscountCode: getDiscountCode,
    trackVisit: trackVisit,
    createRefCode: createRefCode,
    copyToClipboard: copyToClipboard,
    initLanding: initLanding,
    initStart: initStart
  };
})();
